<script setup lang="ts">
import { resetForm } from '@/helpers/ep/form'
import { type FormInstance, type FormRules } from 'element-plus'
import { computed, reactive, ref } from 'vue'
import { useTeams } from '@/composables/core/teams'
import type { ITeam } from '@/types/core/Teams'
import type { ITeamForm } from '@/types/core/forms'

const { createTeamMutation, updateTeamMutation } = useTeams()
const {
  isPending: createIsPending,
  mutateAsync: createAsync,
  isError: createIsError,
  error: createError,
} = createTeamMutation

const {
  isPending: updateIsPending,
  mutateAsync: updateAsync,
  isError: updateIsError,
  error: updateError,
} = updateTeamMutation

const open = defineModel<boolean>()
const props = defineProps<{ teamToUpdate?: ITeam }>()
const emit = defineEmits(['closed'])

const teamFormRef = ref<FormInstance>()
const teamForm = reactive<ITeamForm>({
  name: '',
})

const rules = reactive<FormRules<ITeamForm>>({
  name: [{ required: true, message: 'Name is required.', trigger: 'blur' }],
})

/**
 * Return form loading state (used to disable form).
 */
const formLoading = computed<boolean>(() => {
  return createIsPending.value || updateIsPending.value
})

/**
 * Handles form submit.
 * Create or updates a repair action, depending on the form mode.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  formEl?.validate(async (valid) => {
    if (!valid) {
      return
    }

    try {
      if (props.teamToUpdate) {
        await updateAsync({ id: props.teamToUpdate.id, form: teamForm })
        open.value = false
      } else {
        await createAsync({ ...teamForm })
        open.value = false
      }
    } catch {
      // handled in UI
    }
  })
}

/**
 * Reset form
 */
const handleClosed = () => {
  resetForm(teamFormRef.value)
  createTeamMutation.reset()
  updateTeamMutation.reset()
  emit('closed')
}

const initForm = () => {
  if (props.teamToUpdate) {
    Object.assign(teamForm, props.teamToUpdate)
  }
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    :title="`${teamToUpdate ? 'Update' : 'Create'} Team`"
    :show-close="false"
    @closed="handleClosed"
    @open="initForm"
  >
    <el-form
      ref="teamFormRef"
      :model="teamForm"
      :rules="rules"
      :label-width="160"
      label-position="left"
      :disabled="formLoading"
    >
      <el-form-item label="Name" prop="name">
        <el-input v-model="teamForm.name" placeholder="Team Name" clearable />
      </el-form-item>
    </el-form>
    <rl-error-alert v-if="createIsError" :error="createError" />
    <rl-error-alert v-if="updateIsError" :error="updateError" />
    <template #footer>
      <el-button :disabled="formLoading || !open" @click="open = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="createIsPending || updateIsPending"
        :disabled="formLoading || !open"
        @click="submitForm(teamFormRef)"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>
